import React from "react";
import { Form } from "react-bootstrap";
import { formatBps, formatMinor, formatMinorDelta } from "../../common/format";
import AppScreenPreview from "./AppScreenPreview";

export const MAX_SAMPLES = 12;

const Cell = ({ value, currency }) => <td className="warranty-num">{value === null ? "-" : formatMinor(value, currency)}</td>;

const DeltaCell = ({ delta, currency }) => (
  <td className="warranty-num warranty-delta">
    {typeof delta === "number" ? formatMinorDelta(delta, currency) : "-"}
  </td>
);

// Every number below comes from the server's preview; the panel only lays them side by side.
const SampleTable = ({ entry, currency }) => {
  const { draft, live, delta } = entry;
  const rows = [];
  const draftOr = (value) => (value === undefined ? null : value);

  if (draft || live) {
    rows.push({
      key: "oneTime",
      label: "One-time price",
      draft: draftOr(draft?.oneTime.grossMinor),
      live: draftOr(live?.oneTime.grossMinor),
      delta: delta?.oneTimeGrossMinor,
    });
    const upfrontKeys = [];
    [...(draft?.upfront || []), ...(live?.upfront || [])].forEach((line) => {
      if (!upfrontKeys.some((known) => known.benefitKey === line.benefitKey)) {
        upfrontKeys.push(line);
      }
    });
    upfrontKeys.forEach((line) => {
      rows.push({
        key: `up-${line.benefitKey}`,
        label: line.label,
        draft: draftOr(draft?.upfront.find((l) => l.benefitKey === line.benefitKey)?.grossMinor),
        live: draftOr(live?.upfront.find((l) => l.benefitKey === line.benefitKey)?.grossMinor),
        delta: delta?.upfront.find((l) => l.benefitKey === line.benefitKey)?.grossMinor,
      });
    });
    if (upfrontKeys.length > 0) {
      rows.push({
        key: "oneTimeTotal",
        label: "One-time total with add-ons",
        draft: draftOr(draft?.oneTimeTotal.grossMinor),
        live: draftOr(live?.oneTimeTotal.grossMinor),
        delta: delta?.oneTimeTotalGrossMinor,
      });
    }
    const tenures = [];
    [...(draft?.monthly || []), ...(live?.monthly || [])].forEach((m) => {
      if (!tenures.includes(m.tenureMonths)) {
        tenures.push(m.tenureMonths);
      }
    });
    tenures.sort((a, b) => a - b);
    tenures.forEach((months) => {
      const dm = draft?.monthly.find((m) => m.tenureMonths === months);
      const lm = live?.monthly.find((m) => m.tenureMonths === months);
      const dd = delta?.monthly.find((m) => m.tenureMonths === months);
      rows.push({
        key: `first-${months}`,
        label: `${months} months: first payment`,
        draft: draftOr(dm?.firstPayment.grossMinor),
        live: draftOr(lm?.firstPayment.grossMinor),
        delta: dd?.firstPaymentGrossMinor,
      });
      rows.push({
        key: `inst-${months}`,
        label: `${months} months: each instalment`,
        draft: draftOr(dm?.instalment.grossMinor),
        live: draftOr(lm?.instalment.grossMinor),
        delta: dd?.instalmentGrossMinor,
      });
    });
    if (draft?.couponValueMinor !== null && draft?.couponValueMinor !== undefined) {
      rows.push({
        key: "coupon",
        label: "Coupon value",
        draft: draft.couponValueMinor,
        live: draftOr(live?.couponValueMinor),
        delta: delta?.couponValueMinor,
      });
    } else if (live?.couponValueMinor !== null && live?.couponValueMinor !== undefined) {
      rows.push({ key: "coupon", label: "Coupon value", draft: null, live: live.couponValueMinor, delta: delta?.couponValueMinor });
    }
  }

  return (
    <table className="warranty-preview-table">
      <thead>
        <tr>
          <th scope="col">Line</th>
          <th scope="col" className="warranty-num">Draft</th>
          <th scope="col" className="warranty-num">Live</th>
          <th scope="col" className="warranty-num">Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <th scope="row">{row.label}</th>
            <Cell value={row.draft} currency={currency} />
            <Cell value={row.live} currency={currency} />
            <DeltaCell delta={row.delta} currency={currency} />
          </tr>
        ))}
        {(draft || live) && (
          <>
            <tr>
              <th scope="row">Tax rate</th>
              <td className="warranty-num">{draft ? formatBps(draft.taxRateBps) : "-"}</td>
              <td className="warranty-num">{live ? formatBps(live.taxRateBps) : "-"}</td>
              <td className="warranty-num">-</td>
            </tr>
            <tr>
              <th scope="row">Tax in the one-time price</th>
              <Cell value={draftOr(draft?.oneTime.taxMinor)} currency={currency} />
              <Cell value={draftOr(live?.oneTime.taxMinor)} currency={currency} />
              <td className="warranty-num">-</td>
            </tr>
          </>
        )}
      </tbody>
    </table>
  );
};

// The first sample whose draft side carries the tagline and hero with the placeholders filled in.
// A service that does not resolve them sends neither, and the app screen then shows the text as typed.
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const resolvedSample = (data) =>
  (data?.samples || []).find(
    (entry) => entry.draft && typeof entry.draft === "object" && (hasOwn(entry.draft, "tagline") || hasOwn(entry.draft, "hero"))
  ) || null;

const PreviewPanel = ({
  preview,
  formInvalid,
  content,
  samples,
  candidates,
  onToggleSample,
  search,
  onSearchChange,
  searchState,
  onRetry,
}) => {
  const data = preview.data;
  const currency = data?.currency || "INR";
  const dimmed = preview.status === "loading" || formInvalid;
  // After a failed preview the resolved text is about older content: show the text as typed.
  const resolved = preview.status === "error" ? null : resolvedSample(data);

  return (
    <aside className="warranty-preview" aria-label="Preview">
      <div className="warranty-preview-head">
        <h5>Preview</h5>
        <span className="warranty-preview-status" aria-live="polite">
          {formInvalid
            ? "Waiting for valid numbers"
            : preview.status === "loading"
            ? "Updating"
            : preview.status === "error"
            ? "Not up to date"
            : "Up to date"}
        </span>
      </div>
      <p className="warranty-help">
        Draft prices beside the live version, at the sample cycles below, then the app screen.
        {data ? ` ${data.taxMode === "inclusive" ? "Prices include tax." : "Prices exclude tax."}` : ""}{" "}
        This is a preview; nothing is sold or saved by it.
      </p>

      <details className="warranty-samples">
        <summary>
          Sample cycles ({samples.length} of {MAX_SAMPLES})
        </summary>
        <div className="warranty-samples-body">
          <Form.Control
            type="search"
            size="sm"
            aria-label="Search products for a sample cycle"
            placeholder="Search a product by name (2 letters or more)"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          {searchState.status === "loading" && <div className="warranty-help">Searching</div>}
          {searchState.status === "error" && (
            <div className="warranty-field-msg is-error">{searchState.message}</div>
          )}
          {searchState.status === "ready" && searchState.count === 0 && (
            <div className="warranty-help">No product matches that search.</div>
          )}
          <ul className="warranty-sample-list">
            {candidates.map((candidate) => {
              const checked = samples.some((chosen) => chosen.key === candidate.key);
              const atLimit = !checked && samples.length >= MAX_SAMPLES;
              const onlyOne = checked && samples.length === 1;
              return (
                <li key={candidate.key}>
                  <Form.Check
                    type="checkbox"
                    id={`warranty-sample-${candidate.key}`}
                    checked={checked}
                    disabled={atLimit || onlyOne}
                    label={`${candidate.label} (MRP ${formatMinor(candidate.mrpMinor, currency)})`}
                    onChange={() => onToggleSample(candidate)}
                  />
                </li>
              );
            })}
          </ul>
          {samples.length >= MAX_SAMPLES && (
            <div className="warranty-help">At most {MAX_SAMPLES} samples. Untick one to add another.</div>
          )}
        </div>
      </details>

      <div className={`warranty-preview-body ${dimmed ? "is-dimmed" : ""}`}>
        {preview.status === "error" && (
          <div className="warranty-state is-error" role="alert" style={{ minHeight: 120 }}>
            <div>{preview.message}</div>
            <button type="button" className="warranty-inline-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
        {preview.status === "idle" && !data && (
          <div className="warranty-state" style={{ minHeight: 160 }}>
            <div className="warranty-spinner" aria-hidden="true"></div>
            <div>Pricing the draft</div>
          </div>
        )}
        {data && data.liveVersionId === null && (
          <div className="warranty-help">This tier has no live version yet, so there is nothing to compare with.</div>
        )}
        {data &&
          data.samples.map((entry) => (
            <div className="warranty-sample" key={entry.sample.key}>
              <div className="warranty-sample-head">
                <strong>{entry.sample.label}</strong>
                <span className="warranty-muted">
                  MRP {formatMinor(entry.sample.mrpMinor, currency)}
                </span>
              </div>
              {entry.error && <div className="warranty-field-msg is-error">{entry.error}</div>}
              {(entry.draft || entry.live) && <SampleTable entry={entry} currency={currency} />}
            </div>
          ))}
        {content && (
          <section className="warranty-sample" aria-label="Extend warranty screen in the app">
            <div className="warranty-sample-head">
              <strong>In the app: Extend warranty</strong>
            </div>
            <AppScreenPreview
              content={content}
              resolved={resolved ? resolved.draft : null}
              sampleLabel={resolved ? resolved.sample.label : ""}
            />
          </section>
        )}
      </div>
    </aside>
  );
};

export default PreviewPanel;
